import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { completeOnboarding, getProfile } from "@/lib/relationships.functions";
import { track } from "@/lib/track";
import { getAccessStatus } from "@/lib/access.functions";
import { enqueueDiscovery, setEnrichmentQueryClient } from "@/lib/enrichment-queue";
import { toast } from "sonner";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ContactsPickStep,
  type PickedContact,
} from "@/components/onboarding-contact-picker";
import { MeetYourOrbit } from "@/components/onboarding-meet-orbit";
import { type ProfessionalFocus } from "@/lib/event-taxonomy";
import { OnboardingSetupBridge } from "@/components/onboarding-setup-bridge";
import { OnboardingCalendarConnect } from "@/components/onboarding-calendar-connect";

export const Route = createFileRoute("/onboarding")({ component: Onboarding });

/**
 * One standing goal, in the user's language rather than ours. Each maps to a
 * `professional_focus` preset that re-weights the event taxonomy.
 */
const GOALS: Array<{ value: ProfessionalFocus; label: string; hint: string }> = [
  { value: "founder", label: "Raising or building", hint: "Funding rounds, exits, investor moves" },
  { value: "sales", label: "Winning business", hint: "Budget shifts, new leaders, expansion" },
  { value: "recruiting", label: "Hiring or being hired", hint: "Role changes, team growth, departures" },
  { value: "investor", label: "Finding and backing companies", hint: "Launches, traction, funding news" },
  { value: "banking", label: "Advising on deals", hint: "M&A, strategic reviews, activists" },
  { value: "general", label: "Just staying close", hint: "Anything meaningful to the people I know" },
];

export default function Onboarding() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [focus, setFocus] = useState<ProfessionalFocus | null>(null);
  const [contacts, setContacts] = useState<PickedContact[]>([]);
  const [pickMode, setPickMode] = useState<"native" | "manual">("manual");
  const [autoOpenNative, setAutoOpenNative] = useState(false);
  const [createdContactIds, setCreatedContactIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [phase, setPhase] = useState<"form" | "bridge" | "leaving">("form");
  const finish = useServerFn(completeOnboarding);
  // Auto-redirect to /today is only allowed during the *idle* of step 0 (before
  // the user has done anything). Once they begin onboarding, no network event
  // (window focus, reconnect, token refresh) can route them away.
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  // Invite gate — an unapproved account can't start onboarding either.
  const fetchAccess = useServerFn(getAccessStatus);
  const { data: access } = useQuery({
    queryKey: ["access-status", user?.id],
    queryFn: () => fetchAccess(),
    enabled: !!user,
  });
  useEffect(() => {
    if (access && !access.approved) navigate({ to: "/welcome", replace: true });
  }, [access, navigate]);

  const fetchProfile = useServerFn(getProfile);
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => fetchProfile(),
    enabled: !!user,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (!profile?.onboarded) return;
    if (hasSubmittedRef.current) return;
    if (phase !== "form" || step !== 0) return;
    navigate({ to: "/today" });
  }, [profile, navigate, phase, step]);

  useEffect(() => {
    if (user && !name) {
      const m = (user.user_metadata ?? {}) as Record<string, string>;
      setName(m.full_name || m.name || (user.email ? user.email.split("@")[0] : ""));
    }
  }, [user, name]);

  const submit = async () => {
    hasSubmittedRef.current = true;
    setBusy(true);
    setErr(null);
    // Show the bridge animation immediately — don't make the user stare at a
    // frozen "Setting up…" button while the network call runs.
    setPhase("bridge");
    try {
      await finish({
        data: {
          display_name: name.trim() || undefined,
          professional_focus: focus ?? undefined,
          contacts: contacts.map((c) => ({
            name: c.name.trim(),
            email: c.email?.trim() || null,
            phone: c.phone?.trim() || null,
            category: c.category ?? null,
            company: c.company?.trim() || null,
            is_vip: c.isVip ?? false,
            birthday: c.birthday || null,
          })),
        },
      });
      track("onboarding_completed", { source: "onboarding", people: contacts.length });
      // NOTE: deliberately do NOT invalidate the profile query here — that
      // would flip profile.onboarded → true mid-flow and the auto-redirect
      // would skip the bridge animation. We invalidate at the very end
      // (when the user opens Pyng from the final step).
      // Fetch the created contacts so we can use one for the voice demo step
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: created } = await supabase
          .from("contacts")
          .select("id, name")
          .eq("user_id", user!.id)
          .order("created_at", { ascending: false })
          .limit(contacts.length || 1);
        setCreatedContactIds((created ?? []).map((c) => c.id as string));
        // Same bounded queue the in-app "+ person" batch add uses: identity
        // discovery then the context pass, two at a time, with progress the
        // enrichment strip can show. Rows are already marked queued server-side,
        // so anything this tab doesn't finish is picked up by the worker.
        setEnrichmentQueryClient(qc);
        enqueueDiscovery((created ?? []).map((c) => c.id as string));
      } catch {}
      // Bridge already showing; it will advance to step 2 once ready.
    } catch (e: any) {
      const msg = e?.message ?? "Could not save. Please try again.";
      setErr(msg);
      toast.error(msg);
      // Roll back to the form so the user can retry.
      setPhase("form");
      hasSubmittedRef.current = false;
    } finally {
      setBusy(false);
    }
  };

  const peopleCopy =
    pickMode === "native"
      ? {
          t: "Pick from your contacts",
          d: "Choose the people who matter. Nothing is uploaded in bulk.",
        }
      : {
          t: "Add to your circle",
          d: "Add a name and where they work — that pairing is what lets Pyng find the right person. Even one is enough to start.",
        };

  const steps = [
    // 0 — welcome + name
    {
      t: "Welcome to Pyng",
      d: "A private space for the relationships that matter. We'll set you up in a few taps.",
      body: (
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="w-full rounded-2xl glass-soft px-5 py-4 text-base text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-1 focus:ring-primary"
        />
      ),
    },
    // 1 — one standing goal. Biases which kinds of news are worth your morning.
    {
      t: "What are you working toward?",
      d: "One answer, and Pyng knows which news is worth interrupting you for. You can change it anytime.",
      body: (
        <div className="grid gap-2">
          {GOALS.map((g) => {
            const active = focus === g.value;
            return (
              <button
                key={g.value}
                type="button"
                onClick={() => setFocus(g.value)}
                className={cn(
                  "w-full rounded-2xl px-4 py-3 text-left transition min-h-11",
                  active
                    ? "bg-[var(--accent-neon)]/10 ring-1 ring-[var(--accent-neon)]/60"
                    : "glass-soft hover:bg-white/5",
                )}
              >
                <span className="block text-[15px] font-medium text-foreground">{g.label}</span>
                <span className="block text-xs text-muted-foreground">{g.hint}</span>
              </button>
            );
          })}
        </div>
      ),
    },
    // 2 — contact picker (3 sub-steps)
    {
      t: peopleCopy.t,
      d: peopleCopy.d,
      body: (
        <div>
          <ContactsPickStep
            value={contacts}
            onChange={setContacts}
            mode={pickMode}
            onSwitchMode={(m) => {
              setPickMode(m);
              setAutoOpenNative(false);
            }}
            autoOpenNative={autoOpenNative}
          />
          {contacts.length > 0 && (
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              {contacts.length} {contacts.length === 1 ? "person" : "people"} added
            </p>
          )}
        </div>
      ),
    },
    // 3 — meet your orbit (magic moment)
    {
      t: "Meet your circle",
      d: "Record one quick note, and see what Pyng already found. You can add more anytime.",
      body: (
        createdContactIds.length === 0 ? (
          <div className="space-y-2">
            <div className="h-3 w-2/3 rounded bg-white/5 animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-white/5 animate-pulse" />
            <p className="pt-2 text-xs text-muted-foreground">Still saving your people…</p>
          </div>
        ) : (
          <MeetYourOrbit
            contactIds={createdContactIds}
            contactNames={contacts.map((c) => c.name)}
          />
        )
      ),
    },
    // 4 — calendar (optional, completable later)
    {
      t: "Connect your calendar",
      d: "See who you're meeting this week. Pyng will surface a hook after each meeting — no draft sent without your tap. This step is optional — you can connect anytime from Settings.",
      body: (
        <div className="space-y-3">
          <OnboardingCalendarConnect />
          <p className="text-center text-xs text-muted-foreground leading-relaxed">
            After each meeting, Pyng will quietly ping you for a 10-second voice note — that's how memory grows.
          </p>
        </div>
      ),
    },
  ];

  const current = steps[step];
  const submitStep = 2; // contact picker — we save here so we can use real contactIds in the magic-moment step
  const calendarStep = steps.length - 1;
  const isSubmitStep = step === submitStep;
  const isLastStep = step === calendarStep;

  const canContinue = (() => {
    if (step === 0) return name.trim().length > 0;
    if (step === submitStep) return contacts.length >= 1;
    return true;
  })();

  // goal, meet-orbit and calendar are all skippable
  const showSkip = step === 1 || step === 3 || isLastStep;

  // step 1 sub-step navigation
  const onPrimary = () => {
    if (isLastStep) {
      // Soft hand-off to /today
      setPhase("leaving");
      void qc.invalidateQueries({ queryKey: ["profile"] });
      setTimeout(() => navigate({ to: "/today" }), 650);
      return;
    }
    if (step === submitStep) {
      // pick → submit and advance
      if (contacts.length < 1) {
        toast.error("Add at least one person — name and company — to continue");
        return;
      }
      void submit();
      return;
    }
    setStep(step + 1);
  };

  const onBack = () => {
    setStep(Math.max(0, step - 1));
  };

  const showPrimary = true;
  const primaryLabel = (() => {
    if (isLastStep) return "Open Pyng";
    if (step === submitStep) return busy ? "Setting up…" : "Save";
    if (isSubmitStep) return busy ? "Setting up…" : "Continue";
    return "Continue";
  })();

  // Bridge view between step 1 and step 2
  if (phase === "bridge") {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col items-stretch justify-center px-6 py-12">
        <div className="glass rounded-3xl p-8 animate-in-fade">
          <div className="mb-6 flex gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "h-1 flex-1 rounded-full transition",
                  i <= 2 ? "bg-[var(--accent-neon)]" : "bg-white/10",
                )}
              />
            ))}
          </div>
          <OnboardingSetupBridge
            names={contacts.map((c) => c.name)}
            ready={createdContactIds.length > 0}
            onDone={() => {
              setStep(3);
              setPhase("form");
            }}
          />
        </div>
      </main>
    );
  }

  return (
    <main
      className={cn(
        "mx-auto flex min-h-screen max-w-xl flex-col items-stretch justify-center px-6 py-12 transition-opacity duration-500",
        phase === "leaving" && "opacity-0",
      )}
    >
      <div
        className={cn(
          "glass rounded-3xl p-8 animate-in-fade transition-all duration-500",
          phase === "leaving" && "scale-[0.97] opacity-0",
        )}
      >
        <div className="mb-6 flex gap-1.5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-1 flex-1 rounded-full transition",
                i <= step ? "bg-[var(--accent-neon)]" : "bg-white/10",
              )}
            />
          ))}
        </div>
        <h2 className="text-2xl font-semibold tracking-tight">{current.t}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{current.d}</p>
        {current.body && <div className="mt-6">{current.body}</div>}
        {err && <p className="mt-4 text-sm text-red-400">{err}</p>}
        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            onClick={onBack}
            disabled={step === 0 || busy || phase === "leaving"}
            className="text-sm text-muted-foreground disabled:opacity-30 hover:text-foreground"
          >
            Back
          </button>
          <div className="flex items-center gap-3">
            {showSkip && (
              <button
                onClick={() => {
                  if (isLastStep) {
                    setPhase("leaving");
                    void qc.invalidateQueries({ queryKey: ["profile"] });
                    setTimeout(() => navigate({ to: "/today" }), 650);
                    return;
                  }
                  setStep(step + 1);
                }}
                disabled={busy}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {isLastStep ? "Skip for now" : "Skip"}
              </button>
            )}
            {showPrimary ? (
              <button
                onClick={onPrimary}
                disabled={
                  busy ||
                  phase === "leaving" ||
                  (step === submitStep ? contacts.length < 1 : !canContinue)
                }
                className="rounded-full bg-primary px-6 h-10 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {primaryLabel}
              </button>
            ) : (
              <span />
            )}
          </div>
        </div>
        {phase === "leaving" && (
          <p className="mt-6 text-center text-sm text-muted-foreground animate-in fade-in">
            Welcome in.
          </p>
        )}
      </div>
    </main>
  );
}